import { WithOptional, TRequiredByKeys } from '@/globals';
import { IState } from '@/store/types/search.types';
import { TDashboardBeneficiariyModal } from '@/store/models/dashboard-config.model';
import { IBaseModalProps, TOmitOnClose } from '@/store/types/modals.types';

export type TOmitUserId<T extends IBaseCreateAndEditBeneficiary> = Omit<T, 'userId'>;

export interface IBeneficiary {
  isActive?: boolean;
  metadata?: {
    iban: string;
  };
  tag?: string;
  userId: number;
  nickName?: string;
  name?: string;
  address?: string;
  iban: string;
  bic: string;
  id: number;
  sepaCreditorIdentifier?: string;
  sddB2bWhitelist?: ISddB2bWhitelist[];
  sddCoreBlacklist?: string[];
  // eslint-disable-next-line @typescript-eslint/naming-convention
  usableForSct?: boolean;
  sddCoreKnownUniqueMandateReference?: string[];
  createdDate?: string;
  modifiedDate?: string;
}

export interface ISddB2bWhitelist {
  uniqueMandateReference?: string;
  isRecurrent?: boolean;
  walletId?: number;
}

interface IBaseCreateAndEditBeneficiary {
  userId: number;
  name: string;
  nickName?: string;
  tag?: string;
  iban: string;
  bic: string;
  address?: string;
  isActive?: boolean;
}

export interface ICreateAndEditB2CBeneficiary extends IBaseCreateAndEditBeneficiary {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  usableForSct?: boolean;
  sepaCreditorIdentifier: string;
  sddB2bWhitelist?: Array<never>;
  sddCoreBlacklist: Array<string>;
  sddCoreKnownUniqueMandateReference: Array<string>;
}

export type TUpdateB2CBenenficiary = TOmitUserId<ICreateAndEditB2CBeneficiary>;

export type TCreateAndEditB2BBeneficiary = IBaseCreateAndEditBeneficiary &
  ISddB2bWhitelist & {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    usableForSct?: boolean;
    sepaCreditorIdentifier: string;
    sddB2bWhitelist?: ISddB2bWhitelist[];
    sddCoreBlacklist?: Array<never>;
    sddCoreKnownUniqueMandateReference?: Array<never>;
    walletId?: number;
    isRecurrent?: boolean;
  };

export type TUpdateB2BBeneficiary = TOmitUserId<TCreateAndEditB2BBeneficiary>;

export interface ICreateAndEditSCTEBeneficiary extends IBaseCreateAndEditBeneficiary {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  usableForSct?: boolean;
  sepaCreditorIdentifier?: never;
  sddB2bWhitelist?: never;
  sddCoreBlacklist?: Array<never>;
  sddCoreKnownUniqueMandateReference?: Array<never>;
}

export type TCreateAndEditBeneficiary =
  | ICreateAndEditB2CBeneficiary
  | TCreateAndEditB2BBeneficiary
  | ICreateAndEditSCTEBeneficiary;

export type TCreateBeneficiary = WithOptional<IBeneficiary, 'id'>;

export type TEditBeneficiary = TRequiredByKeys<Partial<IBeneficiary>, 'id' | 'userId'>;

export interface IBeneficiaryModalProps extends IBaseModalProps {
  userId: number;
  beneficiaryId?: number;
  beneficiaryType?: TDashboardBeneficiariyModal;
}

export type TBeneficiaryModalsProps = {
  viewScteBeneficiaryModal: TOmitOnClose<IBeneficiaryModalProps>;
  editScteBeneficiaryModal: TOmitOnClose<IBeneficiaryModalProps>;
  viewB2bB2cBeneficiaryModal: TOmitOnClose<IBeneficiaryModalProps>;
  editB2bBeneficiaryModal: TOmitOnClose<IBeneficiaryModalProps>;
  editB2cBeneficiaryModal: TOmitOnClose<IBeneficiaryModalProps>;
};

export const BeneficiaryModalKeys: Record<
  TDashboardBeneficiariyModal,
  Record<'view' | 'edit', keyof TBeneficiaryModalsProps>
> = {
  scte: {
    view: 'viewScteBeneficiaryModal',
    edit: 'editScteBeneficiaryModal',
  },
  sddrB2B: {
    view: 'viewB2bB2cBeneficiaryModal',
    edit: 'editB2bBeneficiaryModal',
  },
  sddrB2C: {
    view: 'viewB2bB2cBeneficiaryModal',
    edit: 'editB2cBeneficiaryModal',
  },
};

interface IStateBeneficiary {
  beneficiary: IBeneficiary;
  status?: IState;
  contracts?: IState;
}

export interface IEditableContract {
  id: string;
  umr: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  wasBlacklisted: boolean;
  isBlacklisted: boolean;
  isOnEdition: boolean;
  isCreated: boolean;
  index?: number;
}

export type TMetaBeneficiary = {
  maxPerPage?: number;
  pageNumber?: number;
  totalPages?: number;
  totalResults?: number;
  isScte?: boolean;
};

export type TMetaDataBeneficiaries = TMetaBeneficiary;

export type TBeneficiaries = IState<IStateBeneficiary>[];

export interface IFilterBeneficiary {
  id?: number;
  tag?: string;
  userId?: number;
  isActive?: number; // 1 for yes, 0 for no
  nickName?: string;
  address?: string;
  iban?: string; // encrypted IBAN
  IbanInClear?: string;
  bic?: string;
  sepaCreditorIdentifier?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  usableForSct?: boolean;
  createdDate?: string;
  modifiedDate?: string;
  pageNumber?: number;
  name?: string;
  pageCount?: number;
  withMeta?: number;
}

export function buildBeneficiaryRequest(
  beneficiaryType: TDashboardBeneficiariyModal,
  newBeneficiary: TCreateAndEditBeneficiary,
): TCreateAndEditBeneficiary {
  if (isSSDR_B2B(beneficiaryType, newBeneficiary)) {
    return {
      ...newBeneficiary,
      sddB2bWhitelist: [
        {
          uniqueMandateReference: newBeneficiary.uniqueMandateReference,
          isRecurrent: newBeneficiary.isRecurrent,
          walletId: !Number.isNaN(Number(newBeneficiary?.walletId)) ? Number(newBeneficiary.walletId) : undefined,
        },
      ],
    };
  }

  return newBeneficiary;
}

export const isSSDR_B2B = (
  beneficiaryType: TDashboardBeneficiariyModal,
  _newBeneficiary: TCreateAndEditBeneficiary,
): _newBeneficiary is TCreateAndEditB2BBeneficiary => {
  return beneficiaryType === 'sddrB2B';
};
